import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  serverTimestamp, 
  Firestore,
  DocumentReference,
  arrayUnion,
  arrayRemove,
  getDoc,
  Timestamp
} from "firebase/firestore";
import { db, auth } from "./firebaseService";
import { CampPhoto, LocationPhoto } from "@/types/campPhoto";
import { deletePhotoFromStorage } from "./photoStorageService";
import { getUserProfile } from "./firebaseService";
import { updateCampLocation } from "./campLocationUpdateService";
import { forceFirestoreTimestamps } from "@/utils/firestoreUtils";

// Force the client's cached ID token to refresh so any custom claims
// minted during invite acceptance (e.g. role=camp_manager) are visible
// to Firestore rules. Insulates protected writes from the documented
// `setCustomUserClaims` -> fresh-token propagation race.
const ensureFreshAuthToken = async (): Promise<void> => {
  if (auth.currentUser) {
    await auth.currentUser.getIdToken(true);
  }
};

// Create a new photo record
export const createCampPhoto = async (
  data: Omit<CampPhoto, 'id' | 'createdAt' | 'usedBy'>,
  locationId: string,
  userId: string
): Promise<{ photoDoc: DocumentReference, photoId: string }> => {
  try {
    await ensureFreshAuthToken();

    // Create the camp photo document
    const photoData = {
      ...data,
      createdAt: serverTimestamp(),
      createdBy: doc(db, "users", userId),
      usedBy: [doc(db, "campLocations", locationId)]
    };

    // 🔥 CRITICAL: Force all timestamp fields to proper Firestore Timestamps as final step
    const timestampSafeData = forceFirestoreTimestamps(photoData);
    console.log(`[campPhotoService] Applied forceFirestoreTimestamps before creating photo`);

    const photoRef = await addDoc(collection(db, "campPhotos"), timestampSafeData);
    console.log("Camp photo created with ID:", photoRef.id);
    
    return {
      photoDoc: photoRef,
      photoId: photoRef.id
    };
  } catch (error) {
    console.error("Error creating camp photo:", error);
    throw error;
  }
};

// Update an existing photo
export const updateCampPhoto = async (
  photoId: string,
  data: Partial<CampPhoto>
) => {
  try {
    // 🔥 CRITICAL: Force all timestamp fields to proper Firestore Timestamps as final step
    const timestampSafeData = forceFirestoreTimestamps(data);
    console.log(`[campPhotoService] Applied forceFirestoreTimestamps before updating photo`);
    
    const photoRef = doc(db, "campPhotos", photoId);
    await updateDoc(photoRef, timestampSafeData);
    console.log("Camp photo updated:", photoId);
    return true;
  } catch (error) {
    console.error("Error updating camp photo:", error);
    throw error;
  }
};

// Delete a photo if not used by other locations
export const deleteCampPhoto = async (photoId: string) => {
  try {
    const photoRef = doc(db, "campPhotos", photoId);
    const photoSnapshot = await getDoc(photoRef);
    
    if (!photoSnapshot.exists()) {
      throw new Error(`Photo with ID ${photoId} not found`);
    }
    
    const photoData = photoSnapshot.data() as CampPhoto;
    
    // Check if the photo is used by other locations
    if (photoData.usedBy && photoData.usedBy.length <= 1) {
      // Photo is not used by other locations, delete from storage
      if (photoData.path) {
        await deletePhotoFromStorage(photoData.path);
      }
      
      // Delete the photo document
      await deleteDoc(photoRef);
      console.log("Photo deleted from storage and database:", photoId);
    } else {
      // Photo is still used by other locations, just delete this reference
      console.log("Photo is used by other locations, keeping in database");
    }
    
    return true;
  } catch (error) {
    console.error("Error deleting camp photo:", error);
    throw error;
  }
};

// Get photos for a specific organization
export const getOrganizationPhotos = async (orgId: string) => {
  try {
    const q = query(
      collection(db, "campPhotos"), 
      where("organizationId", "==", orgId)
    );
    
    const querySnapshot = await getDocs(q);
    const photos: CampPhoto[] = [];
    
    querySnapshot.forEach((doc) => {
      photos.push({
        id: doc.id,
        ...doc.data() as Omit<CampPhoto, 'id'>
      });
    });
    
    return photos;
  } catch (error) {
    console.error("Error getting organization photos:", error);
    throw error;
  }
};

// Link a photo to a location
export const linkPhotoToLocation = async (photoId: string, locationId: string) => {
  try {
    const photoRef = doc(db, "campPhotos", photoId);
    const locationRef = doc(db, "campLocations", locationId);
    
    await updateDoc(photoRef, {
      usedBy: arrayUnion(locationRef)
    });
    
    return true;
  } catch (error) {
    console.error("Error linking photo to location:", error);
    throw error;
  }
};

// Unlink a photo from a location
export const unlinkPhotoFromLocation = async (photoId: string, locationId: string) => {
  try {
    const photoRef = doc(db, "campPhotos", photoId);
    const locationRef = doc(db, "campLocations", locationId);
    
    await updateDoc(photoRef, {
      usedBy: arrayRemove(locationRef)
    });
    
    return true;
  } catch (error) {
    console.error("Error unlinking photo from location:", error);
    throw error;
  }
};

// Add a photo to a location's latest version
export const addPhotoToLocationVersion = async (
  locationId: string,
  userId: string,
  locationPhoto: LocationPhoto
) => {
  try {
    await ensureFreshAuthToken();

    // Get the current location data
    const locationRef = doc(db, "campLocations", locationId);
    const locationSnapshot = await getDoc(locationRef);

    if (!locationSnapshot.exists()) {
      throw new Error(`Location with ID ${locationId} not found`);
    }

    const locationData = locationSnapshot.data();

    // Get the current photos array or initialize it
    const currentPhotos = locationData.photos || [];

    // Add the new photo to the array
    const updatedPhotos = [...currentPhotos, locationPhoto];

    // Update the location with the new photos array
    await updateCampLocation(
      db,
      locationId,
      { photos: updatedPhotos },
      userId,
      "Added photo to location",
      true
    );

    return true;
  } catch (error) {
    console.error("Error adding photo to location version:", error);
    throw error;
  }
};

/** Insert a photo at index 0 (header slot) on the location draft / latest version. */
export const prependPhotoToLocationVersion = async (
  locationId: string,
  userId: string,
  locationPhoto: LocationPhoto
) => {
  try {
    const locationRef = doc(db, "campLocations", locationId);
    const locationSnapshot = await getDoc(locationRef);

    if (!locationSnapshot.exists()) {
      throw new Error(`Location with ID ${locationId} not found`);
    }

    const locationData = locationSnapshot.data();
    const currentPhotos = locationData.photos || [];
    const updatedPhotos = [locationPhoto, ...currentPhotos];

    await updateCampLocation(
      db,
      locationId,
      { photos: updatedPhotos },
      userId,
      "Prepended photo from pasted JSON (admin)",
      true
    );

    return true;
  } catch (error) {
    console.error("Error prepending photo to location version:", error);
    throw error;
  }
};

// Update a photo in a location's latest version
export const updatePhotoInLocationVersion = async (
  locationId: string,
  userId: string,
  photoIndex: number,
  updates: Partial<LocationPhoto>
) => {
  try {
    // Get the current location data
    const locationRef = doc(db, "campLocations", locationId);
    const locationSnapshot = await getDoc(locationRef);
    
    if (!locationSnapshot.exists()) {
      throw new Error(`Location with ID ${locationId} not found`);
    }
    
    const locationData = locationSnapshot.data();
    
    // Get the current photos array
    const currentPhotos = locationData.photos || [];
    
    // Ensure the index is valid
    if (photoIndex < 0 || photoIndex >= currentPhotos.length) {
      throw new Error(`Invalid photo index: ${photoIndex}`);
    }
    
    // Update the photo at the specified index
    const updatedPhotos = [...currentPhotos];
    
    // Make sure all properties are set with proper fallback values
    updatedPhotos[photoIndex] = {
      ...updatedPhotos[photoIndex],
      path: updatedPhotos[photoIndex].path || "",
      photoReference: updates.photoReference || updatedPhotos[photoIndex].photoReference || "",
      caption: updates.caption !== undefined ? updates.caption : (updatedPhotos[photoIndex].caption || ""),
      altText: updates.altText !== undefined ? updates.altText : (updatedPhotos[photoIndex].altText || ""),
      yOffset: updates.yOffset !== undefined ? updates.yOffset : (updatedPhotos[photoIndex].yOffset || 0),
      campPhotoId: updatedPhotos[photoIndex].campPhotoId
    };
    
    // Update the location with the updated photos array
    await updateCampLocation(
      db,
      locationId,
      { photos: updatedPhotos },
      userId,
      "Updated photo in location",
      true
    );
    
    return true;
  } catch (error) {
    console.error("Error updating photo in location version:", error);
    throw error;
  }
};

// Remove a photo from a location's latest version
export const removePhotoFromLocationVersion = async (
  locationId: string,
  userId: string,
  photoIndex: number
) => {
  try {
    // Get the current location data
    const locationRef = doc(db, "campLocations", locationId);
    const locationSnapshot = await getDoc(locationRef);
    
    if (!locationSnapshot.exists()) {
      throw new Error(`Location with ID ${locationId} not found`);
    }
    
    const locationData = locationSnapshot.data();
    
    // Get the current photos array
    const currentPhotos = locationData.photos || [];
    
    // Ensure the index is valid
    if (photoIndex < 0 || photoIndex >= currentPhotos.length) {
      throw new Error(`Invalid photo index: ${photoIndex}`);
    }
    
    // Get the photo info before removing it
    const photoToRemove = currentPhotos[photoIndex];
    
    // Remove the photo at the specified index
    const updatedPhotos = currentPhotos.filter((_, i) => i !== photoIndex);
    
    // Update the location with the updated photos array
    await updateCampLocation(
      db,
      locationId,
      { photos: updatedPhotos },
      userId,
      "Removed photo from location",
      true
    );
    
    // If this photo has a reference to a campPhoto document, unlink/delete unless it's a placeholder
    if (photoToRemove.campPhotoId) {
      // Extract the ID from the reference
      const campPhotoId = typeof photoToRemove.campPhotoId === 'string' 
        ? photoToRemove.campPhotoId 
        : photoToRemove.campPhotoId.id;

      // Skip storage/campPhoto mutations for placeholder entries
      const isPlaceholder = campPhotoId === '_placeholder' || campPhotoId === 'campPhotos/_placeholder';
      if (!isPlaceholder) {
        await unlinkPhotoFromLocation(campPhotoId, locationId);
        // Consider deleting the photo if it's not used anywhere else
        await deleteCampPhoto(campPhotoId);
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error removing photo from location version:", error);
    throw error;
  }
};

// Reorder photos in a location's latest version
export const reorderPhotosInLocationVersion = async (
  locationId: string,
  userId: string,
  newOrder: number[]
) => {
  try {
    // Get the current location data
    const locationRef = doc(db, "campLocations", locationId);
    const locationSnapshot = await getDoc(locationRef);
    
    if (!locationSnapshot.exists()) {
      throw new Error(`Location with ID ${locationId} not found`);
    }
    
    const locationData = locationSnapshot.data();
    
    // Get the current photos array
    const currentPhotos = locationData.photos || [];
    
    // Validate new order
    if (newOrder.length !== currentPhotos.length || 
        !newOrder.every(i => i >= 0 && i < currentPhotos.length)) {
      throw new Error("Invalid reordering indices");
    }
    
    // Create reordered array
    const reorderedPhotos = newOrder.map(i => currentPhotos[i]);
    
    // Update the location with the reordered photos array
    await updateCampLocation(
      db,
      locationId,
      { photos: reorderedPhotos },
      userId,
      "Reordered photos in location",
      true
    );
    
    return true;
  } catch (error) {
    console.error("Error reordering photos in location version:", error);
    throw error;
  }
};

// Get placeholder photos from Firestore
export const getPlaceholderPhotos = async (): Promise<string[]> => {
  try {
    console.log("Fetching placeholder photos from campPhotos/_headerPlaceholders");
    
    const placeholderRef = doc(db, "campPhotos", "_headerPlaceholders");
    const placeholderSnapshot = await getDoc(placeholderRef);
    
    if (!placeholderSnapshot.exists()) {
      console.warn("Placeholder photos document not found");
      return [];
    }
    
    const data = placeholderSnapshot.data();
    const urls = data?.urls || [];
    
    console.log(`Found ${urls.length} placeholder photos`);
    return urls;
  } catch (error) {
    console.error("Error fetching placeholder photos:", error);
    throw error;
  }
};

// Add a placeholder photo to a location's latest version
export const addPlaceholderPhotoToLocation = async (
  locationId: string,
  userId: string,
  placeholderUrl: string
): Promise<boolean> => {
  try {
    console.log(`Adding placeholder photo to location ${locationId}:`, placeholderUrl);

    // Get the current location data
    const locationRef = doc(db, "campLocations", locationId);
    const locationSnapshot = await getDoc(locationRef);
    
    if (!locationSnapshot.exists()) {
      throw new Error(`Location with ID ${locationId} not found`);
    }
    
    const locationData = locationSnapshot.data();
    
    // Get the current photos array or initialize it
    const currentPhotos = locationData.photos || [];
    
    // 🔥 CRITICAL FIX: Firebase does NOT allow serverTimestamp() inside arrays!
    // We must use Timestamp.now() instead for array elements
    const now = Timestamp.now();
    
    // Create the new placeholder photo object according to spec
    const placeholderPhoto: LocationPhoto = {
      createdAt: now, // Use Timestamp.now() instead of serverTimestamp() for arrays
      createdBy: doc(db, "users", userId), // Document reference to current user
      order: 0, // Set to 0 for the first image
      path: placeholderUrl, // URL of the selected placeholder image
      permissionStatus: "granted", // Set to "granted" per spec
      photoReference: "from Canva", // Set to "from Canva" per spec
      yOffset: 0, // Set to 0 per spec
      campPhotoId: doc(db, "campPhotos", "_placeholder") // Dummy reference for placeholder
    };

    // Insert at the beginning of the array to make it the first/header photo
    const updatedPhotos = [placeholderPhoto, ...currentPhotos];
    
    console.log(`[campPhotoService] Created placeholder photo with Timestamp.now() to avoid Firebase array limitation`);
    
    // Update the location with the new photos array
    await updateCampLocation(
      db,
      locationId,
      { photos: updatedPhotos },
      userId,
      "Photos updated", // Change description per spec
      true // Force update to latest draft
    );
    
    console.log("Successfully added placeholder photo to location");
    return true;
  } catch (error) {
    console.error("Error adding placeholder photo to location:", error);
    throw error;
  }
};

// Get a single camp photo by ID
export const getCampPhoto = async (photoId: string): Promise<CampPhoto | null> => {
  try {
    const photoRef = doc(db, "campPhotos", photoId);
    const photoSnap = await getDoc(photoRef);
    
    if (photoSnap.exists()) {
      return {
        id: photoSnap.id,
        ...photoSnap.data()
      } as CampPhoto;
    }
    
    return null;
  } catch (error) {
    console.error("Error getting camp photo:", error);
    throw error;
  }
};
